import { useAppSelector } from '@common/reducers/hooks/store.hook';
import { makeSelectDisplayNamesByContactIds, selectChatById } from './redux/TeamInboxSelectors';
import {
    Block as BlockIcon,
    CameraAlt as CameraAltIcon,
    Campaign as CampaignIcon,
    Description as DescriptionIcon,
    Headset as HeadsetIcon,
    LocationOn as LocationOnIcon,
    MicNone as MicNoneIcon,
    Note as NoteIcon,
    Person as PersonIcon,
    Videocam as VideocamIcon
} from '@mui/icons-material';
import { Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { authorIdOf, extractContactIdsFromMessage, getMessageText } from './team-inbox-helpers';
import classes from './team-inbox.module.scss';

type NotificationProps = {
    waChatId: string;
};

export const NotificationAndLastMessageView = React.memo(({
    waChatId,
}: NotificationProps) => {

    const waChat = useAppSelector((state) => selectChatById(state, waChatId));
    const waSessionPhoneNumber = useAppSelector(state => state.TeamInboxReducer?.waSessionPhoneNumber);

    const lastMessage = waChat?.lastMessage;

    // Memoize contact IDs extraction to prevent recalculation on every render
    const contactIds = useMemo(() => {
        if (!lastMessage) return [];
        return extractContactIdsFromMessage({
            author: authorIdOf(lastMessage),
            type: lastMessage.type,
            subtype: lastMessage.subtype,
            revokeSender: lastMessage.revokeSender?._serialized,
            recipients: lastMessage.recipients,
        });
    }, [lastMessage]);

    // Memoize the selector to prevent creating new selector on every render
    const selectDisplayNames = useMemo(
        () => makeSelectDisplayNamesByContactIds(contactIds),
        [contactIds]
    );
    const displayNames = useAppSelector(selectDisplayNames);

    const isUnread = waChat?.unreadCount > 0;
    const isSystemMessage = lastMessage?.type === 'gp2';


    const getMessageIcon = (message: { type?: string; ack?: number; fromMe?: boolean; isLive?: boolean; subtype?: string } | null | undefined): React.ReactNode => {
        if (!message) return null;
        const iconStyle = { fontSize: "1.3rem", marginRight: "4px" };

        switch (message.type) {
            case "audio":
                return <HeadsetIcon sx={iconStyle} />;

            case "ptt":
                const pttIconColor = (() => {
                    if (message.ack === 4) {
                        return "#53bdeb"; // Blue color for read messages
                    } else if (!message.fromMe) {
                        return "#09d261"; // Green color for incoming messages
                    }
                    // Return null to use the default icon color
                    return null;
                })();

                return <MicNoneIcon
                    sx={{
                        ...iconStyle,
                        ...(pttIconColor && { color: pttIconColor })
                    }} />;

            case "image":
                return <CameraAltIcon sx={iconStyle} />;

            case "video":
                return <VideocamIcon sx={iconStyle} />;

            case "document":
                return <DescriptionIcon sx={iconStyle} />;

            case "sticker":
                return <NoteIcon sx={{ ...iconStyle, transform: 'rotate(90deg)' }} />;

            case "location":
                if (message.isLive) {
                    return <LocationOnIcon sx={iconStyle} />;
                } else {
                    return <LocationOnIcon sx={iconStyle} />;
                }

            case "vcard":
                return <PersonIcon sx={iconStyle} />;

            case "revoked":
                return <BlockIcon sx={{ ...iconStyle, transform: 'rotate(90deg)' }} />;

            case "gp2":
                if (message.subtype === 'announce')
                    return <CampaignIcon sx={iconStyle} />
                return null;

            default:
                return null;
        }
    }

    const renderContent = () => {
        if (!lastMessage) return null;

        const messageText = getMessageText(lastMessage, displayNames, waSessionPhoneNumber ?? '');
        return (
            <>
                {getMessageIcon(lastMessage)}
                <Typography
                    variant="body2"
                    className={isUnread ? classes.unreadMessagePreviewText : classes.readMessagePreviewText}
                    style={{
                        unicodeBidi: isSystemMessage ? 'isolate' : 'plaintext',
                    }}
                    dir="auto"
                    title={messageText}
                >
                    {messageText}
                </Typography>
            </>
        );
    }

    return renderContent();
});
